import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Check, AlertCircle, Loader2, ClipboardList, ArrowLeft, ArrowRight, Plus, Trash2, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo, Button } from '../components';
import { inputCls, labelCls, cardCls } from './publicFormStyles';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * The assessor's form — PUBLIC, opened from an emailed one-time link.
 *
 * Ten clinical sections plus a review step, drawn entirely from the schema the
 * API sends. Nothing about the questions is hard-coded here: the page renders
 * whatever sections, fields and conditions it is given, so the form on screen
 * and the form the API validates are the same form by construction.
 *
 * Written for the real reader: somebody standing in a front room with a phone,
 * who did not ask to be here and may not finish in one sitting. Hence a saved
 * draft on the server (not just this browser), a section at a time rather than
 * one endless page, and validation that only bites when they try to move on.
 */

const Shell = ({ children, wide = false }) => (
  <div className="min-h-screen bg-[#F5F9FF] dark:bg-[#1b1f26] px-4 py-8 sm:py-10">
    <div className={`${wide ? 'max-w-[1180px]' : 'max-w-[900px]'} mx-auto`}>
      <div className="mb-6 flex justify-center"><Logo /></div>
      {children}
    </div>
  </div>
);

const Message = ({ tone = 'amber', title, children }) => (
  <Shell>
    <div className={`${cardCls} text-center`}>
      {tone === 'done' ? (
        <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mx-auto mb-4">
          <Check size={28} className="text-emerald-600" />
        </div>
      ) : <AlertCircle size={34} className="mx-auto text-amber-500 mb-3" />}
      <h1 className="text-[19px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">{title}</h1>
      <p className="text-[14px] text-[#64748B] dark:text-gray-400 leading-relaxed">{children}</p>
    </div>
  </Shell>
);

const today = () => new Date().toISOString().slice(0, 10);
const NO_FUTURE = ['dob', 'assessmentDate', 'measured', 'mustDate', 'skinDate'];
const isFilled = (v) => (Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim().length > 0 : v !== undefined && v !== null && v !== '');

/** Mirrors the API's fieldError, so the assessor is never told something the server disagrees with. */
function fieldError(field, value) {
  if (field.required && !isFilled(value)) return `Complete ${field.label.toLowerCase()}.`;
  if (!isFilled(value)) return '';
  const text = String(value);
  if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return 'Enter a valid email address.';
  if (field.type === 'number') {
    const n = Number(text);
    if (!Number.isFinite(n) || n < (field.min ?? 0) || n > (field.max ?? 1e8)) return 'Enter a valid number within the field range.';
  }
  if (field.id === 'nhs' && !/^\d{10}$/.test(text.replace(/\s/g, ''))) return 'Use 10 digits, or leave blank until confirmed.';
  if (NO_FUTURE.includes(field.id) && text > today()) return 'This date cannot be in the future.';
  return '';
}

/**
 * One row of the desktop section rail: number (or a tick once the section is
 * done), title, and how many of its essential fields are answered.
 */
const RailItem = ({ n, title, subtitle, active, complete, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={active ? 'step' : undefined}
    className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-xl text-left transition ${active
      ? 'bg-blue-50 dark:bg-blue-900/20'
      : 'hover:bg-[#F3F6FB] dark:hover:bg-[#31373f]'}`}
  >
    <span
      className={`mt-[1px] w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10.5px] font-bold tabular-nums ${active
        ? 'bg-blue-600 text-white'
        : complete
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          : 'bg-[#EEF2F8] text-[#94A3B8] dark:bg-[#31373f] dark:text-gray-400'}`}
    >
      {complete && !active ? <Check className="w-3.5 h-3.5" /> : String(n).padStart(2, '0')}
    </span>
    <span className="min-w-0">
      <span className={`block text-[13px] font-semibold leading-snug ${active
        ? 'text-blue-700 dark:text-blue-300'
        : 'text-[#475569] dark:text-gray-300'}`}>{title}</span>
      <span className="block text-[11px] text-[#94A3B8] mt-0.5 leading-snug">{subtitle}</span>
    </span>
  </button>
);

const CareAssessmentForm = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [attested, setAttested] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const sections = data?.sections || [];
  const repeats = data?.repeatSchemas || {};
  const reviewStep = sections.length;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/care-assessment/${token}`);
        const body = await response.json();
        if (!active) return;
        if (!response.ok || !body.success) {
          setFatal(body.message || 'This link could not be opened.');
          return;
        }
        setData(body.data);
        setAnswers(body.data.answers || {});
        setSavedAt(body.data.saved_at || null);
        if (body.data.submitted) setDone({ already: true });
      } catch {
        if (active) setFatal('We could not reach the server. Please check your connection and try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  // Leaving with unsaved answers would lose a home visit's worth of work.
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = useCallback((patch) => {
    setAnswers((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  /** A field is on screen only when its controlling answer says so. */
  const visible = useCallback((field) => !field.when || field.when.values.includes(answers?.[field.when.id]), [answers]);

  /** Everything wrong in one section — same rules as the API. */
  const sectionIssues = useCallback((index) => {
    const section = sections[index];
    const issues = [];
    if (!section) return issues;
    section.groups.forEach((grp) => grp.fields.filter(visible).forEach((field) => {
      if (field.type === 'repeat') {
        const rows = answers[field.id] || [];
        const need = {
          contacts: [answers.contactStatus === 'Contact details available', 'Add at least one contact.'],
          allergies: [answers.allergyStatus === 'Known allergies / sensitivities', 'Add the known allergy and reaction.'],
          medicines: [answers.medicationStatus === 'Current medicines to record', 'Add the current medicines.'],
        }[field.id];
        if (need && need[0] && !rows.length) issues.push({ id: field.id, message: need[1] });
        rows.forEach((row, n) => (repeats[field.kind] || []).forEach((col) => {
          const error = fieldError(col, row?.[col.id]);
          if (error) issues.push({ id: `${field.id}-${n}-${col.id}`, message: error });
        }));
      } else {
        const error = fieldError(field, answers[field.id]);
        if (error) issues.push({ id: field.id, message: error });
      }
    }));
    if (section.id === 'nutrition'
      && ((answers.foodTexture && answers.foodTexture !== 'Not recorded') || (answers.fluidTexture && answers.fluidTexture !== 'Not recorded'))
      && !String(answers.textureSource || '').trim()) {
      issues.push({ id: 'textureSource', message: 'Record the professional source and date for the prescribed texture.' });
    }
    if (section.id === 'health' && answers.medicationStatus === 'Current medicines to record' && !String(answers.medSource || '').trim()) {
      issues.push({ id: 'medSource', message: 'Record the source and date of the medication information.' });
    }
    return issues;
  }, [sections, repeats, answers, visible]);

  /** Essential-field progress, per section and overall. */
  const statsFor = useCallback((index) => {
    const section = sections[index];
    let total = 0;
    let doneCount = 0;
    if (!section) return { total, done: doneCount };
    section.groups.flatMap((grp) => grp.fields).filter(visible).forEach((field) => {
      if (field.type === 'repeat') {
        const rows = answers[field.id] || [];
        if (!rows.length && ['contacts', 'allergies', 'medicines'].includes(field.id)) total += 1;
        rows.forEach((row) => (repeats[field.kind] || []).filter((c) => c.required).forEach((col) => {
          total += 1;
          if (!fieldError(col, row?.[col.id])) doneCount += 1;
        }));
      } else if (field.required) {
        total += 1;
        if (!fieldError(field, answers[field.id])) doneCount += 1;
      }
    });
    return { total, done: doneCount };
  }, [sections, repeats, answers, visible]);

  const allIssues = useMemo(
    () => sections.flatMap((_, i) => sectionIssues(i).map((x) => ({ ...x, section: i }))),
    [sections, sectionIssues],
  );

  const overall = useMemo(() => sections.reduce((acc, _, i) => {
    const st = statsFor(i);
    return { done: acc.done + st.done, total: acc.total + st.total };
  }, { done: 0, total: 0 }), [sections, statsFor]);

  const conflicts = useMemo(() => (
    answers.medicationStatus !== 'No current medicines (confirmed)' && answers.reconciliation === 'Not applicable – confirmed no medicines'
      ? ['Medication status conflicts with the medication review answer.'] : []
  ), [answers]);

  const send = async (submit) => {
    const setBusy = submit ? setSubmitting : setSaving;
    setBusy(true);
    try {
      const response = await fetch(`${API_URL}/care-assessment/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, submit }),
      });
      const body = await response.json();
      if (!response.ok || !body.success) {
        toast.error(body.message || 'Could not save your assessment.');
        return false;
      }
      setDirty(false);
      if (submit) { setDone({}); return true; }
      setSavedAt(body.data?.saved_at || new Date().toISOString());
      toast.success('Draft saved. You can come back to this link.');
      return true;
    } catch {
      toast.error('We could not reach the server. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const goTo = (index, showErrors = false) => {
    const next = Math.max(0, Math.min(reviewStep, index));
    const found = {};
    if (showErrors && next <= reviewStep) sectionIssues(step).forEach((x) => { found[x.id] = x.message; });
    setErrors(found);
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const next = () => {
    const issues = sectionIssues(step);
    if (issues.length) {
      const found = {};
      issues.forEach((x) => { found[x.id] = x.message; });
      setErrors(found);
      toast.error('Complete the highlighted fields before moving on.');
      return;
    }
    goTo(step + 1);
  };

  const submit = async () => {
    if (allIssues.length) {
      toast.error('Complete the highlighted fields before submitting.');
      goTo(allIssues[0].section, true);
      return;
    }
    if (conflicts.length) { toast.error(conflicts[0]); return; }
    if (!attested) { toast.error('Confirm your review before submitting.'); return; }
    await send(true);
  };

  if (loading) {
    return <Shell><div className={`${cardCls} text-center`}><Loader2 size={30} className="mx-auto animate-spin text-blue-600" /></div></Shell>;
  }
  if (fatal) return <Message title="This link can’t be opened">{fatal}</Message>;
  if (done) {
    return (
      <Message tone="done" title={done.already ? 'This assessment has already been sent' : 'Thank you'}>
        {done.already
          ? 'There is nothing more you need to do.'
          : `Your assessment has been sent to ${data.organisation.name} for review. The manager makes the final decision. There is nothing more you need to do.`}
      </Message>
    );
  }

  const { person, organisation, request, setting } = data;

  /* ── Controls ──────────────────────────────────────────────────────────── */
  const control = (field, value, id, onChange) => {
    const invalid = !!errors[id];
    const cls = `${inputCls} ${invalid ? '!border-rose-400 !ring-2 !ring-rose-100' : ''}`;
    const common = {
      id,
      'aria-invalid': invalid || undefined,
      'aria-describedby': (field.hint || invalid) ? `${field.hint ? `hint-${id}` : ''} ${invalid ? `err-${id}` : ''}`.trim() : undefined,
      'aria-required': field.required || undefined,
    };

    if (field.type === 'select') {
      return (
        <select {...common} className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select an option</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.type === 'textarea') {
      return (
        <textarea {...common} rows={3} className={cls} placeholder={field.placeholder || 'Add relevant details…'}
          value={value || ''} onChange={(e) => onChange(e.target.value)} />
      );
    }
    if (field.type === 'checks') {
      const picked = value || [];
      return (
        <div role="group" aria-label={field.label} className="flex flex-col gap-2 pt-1">
          {field.options.map((o) => (
            <label key={o} className="flex items-start gap-2.5 text-[14px] sm:text-[13.5px] text-[#475569] dark:text-gray-300 leading-snug">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0"
                checked={picked.includes(o)}
                onChange={(e) => onChange(e.target.checked ? [...picked, o] : picked.filter((x) => x !== o))}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    }
    return (
      <input
        {...common}
        type={field.type || 'text'}
        className={cls}
        value={value || ''}
        placeholder={field.placeholder || ''}
        min={field.min}
        max={NO_FUTURE.includes(field.id) ? today() : field.max}
        step={field.step}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  const labelled = (field, value, id, onChange) => (
    <div key={id} className={['textarea', 'checks'].includes(field.type) ? 'sm:col-span-2' : ''}>
      <label className={labelCls} htmlFor={id}>
        {field.label}{field.required && <span className="text-rose-500" aria-label="required"> *</span>}
      </label>
      {control(field, value, id, onChange)}
      {field.hint && <p id={`hint-${id}`} className="text-[12px] text-[#94A3B8] mt-1.5">{field.hint}</p>}
      {errors[id] && <p id={`err-${id}`} className="text-[12px] text-rose-600 mt-1.5">{errors[id]}</p>}
    </div>
  );

  /** A repeating block — contacts, allergies, medicines, wounds, actions. */
  const repeatBlock = (field) => {
    const rows = answers[field.id] || [];
    const schema = repeats[field.kind] || [];
    const noun = { contacts: 'contact', allergies: 'allergy', medicines: 'medicine', wounds: 'wound', actions: 'action' }[field.kind] || 'entry';
    const update = (n, key, v) => {
      const copy = rows.map((row, i) => (i === n ? { ...row, [key]: v } : row));
      set({ [field.id]: copy });
    };
    return (
      <div key={field.id} className="sm:col-span-2">
        {errors[field.id] && <p className="text-[12px] text-rose-600 mb-2">{errors[field.id]}</p>}
        {rows.length === 0 ? (
          <p className="text-[13px] text-[#94A3B8] bg-[#F8FAFF] dark:bg-[#1f242b] border border-dashed border-[#DCE5F3] dark:border-gray-700 rounded-xl px-4 py-5 text-center">
            No {field.label.toLowerCase()} added yet.
          </p>
        ) : rows.map((row, n) => (
          <div key={n} className="border border-[#E3E9F3] dark:border-gray-700 rounded-xl p-4 mb-3">
            <div className="flex items-center justify-between gap-3 mb-3">
              <strong className="text-[13px] font-semibold text-[#475569] dark:text-gray-300">{field.label} · {n + 1}</strong>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rose-600 hover:text-rose-700"
                onClick={() => set({ [field.id]: rows.filter((_, i) => i !== n) })}
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {schema.map((col) => labelled(col, row?.[col.id], `${field.id}-${n}-${col.id}`, (v) => update(n, col.id, v)))}
            </div>
          </div>
        ))}
        <Button variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => set({ [field.id]: [...rows, {}] })}>
          Add {noun}
        </Button>
      </div>
    );
  };

  const renderField = (field) => {
    if (!visible(field)) return null;
    if (field.type === 'repeat') return repeatBlock(field);
    return labelled(field, answers[field.id], field.id, (v) => {
      // Clearing the status that a repeating block hangs off must not silently
      // strip rows the assessor has already filled in.
      const clears = { allergyStatus: ['allergies', 'Known allergies / sensitivities'], medicationStatus: ['medicines', 'Current medicines to record'], contactStatus: ['contacts', 'Contact details available'] }[field.id];
      if (clears && v !== clears[1] && (answers[clears[0]] || []).some((row) => Object.values(row).some(isFilled))) {
        if (!window.confirm(`Changing this answer will remove the recorded ${clears[0]}. Continue?`)) return;
        set({ [field.id]: v, [clears[0]]: [] });
        return;
      }
      // Choosing the status that needs rows opens the first one straight away.
      if (clears && v === clears[1] && !(answers[clears[0]] || []).length) {
        set({ [field.id]: v, [clears[0]]: [{}] });
        return;
      }
      set({ [field.id]: v });
      setErrors((prev) => { const copy = { ...prev }; delete copy[field.id]; return copy; });
    });
  };

  /* ── Review step ───────────────────────────────────────────────────────── */
  const review = () => (
    <>
      <header className="mb-5">
        <div className="text-[10px] font-bold tracking-wider text-blue-600 mb-2">STEP {reviewStep + 1} OF {reviewStep + 1}</div>
        <h2 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">Review &amp; return</h2>
        <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
          Your recommendation supports the manager’s decision. It does not approve admission or start care.
        </p>
      </header>

      <div className={`rounded-xl p-4 mb-5 border text-[13px] ${allIssues.length || conflicts.length
        ? 'bg-amber-50 border-amber-100 text-amber-800 dark:bg-amber-900/10 dark:border-amber-900/40 dark:text-amber-300'
        : 'bg-emerald-50 border-emerald-100 text-emerald-800 dark:bg-emerald-900/10 dark:border-emerald-900/40 dark:text-emerald-300'}`}
      >
        <strong>{allIssues.length ? `${allIssues.length} field${allIssues.length === 1 ? '' : 's'} need attention` : conflicts.length ? 'Check your answers' : 'Ready for your confirmation'}</strong>
        <p className="mt-1 leading-relaxed">
          {allIssues.length
            ? 'Open the highlighted sections to complete the assessment. You can save a draft at any time.'
            : 'Check your answers and recommendation before submitting.'}
        </p>
        {conflicts.length > 0 && <ul className="list-disc ml-5 mt-2">{conflicts.map((c) => <li key={c}>{c}</li>)}</ul>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {sections.map((section, i) => {
          const issues = sectionIssues(i);
          return (
            <article key={section.id} className="border border-[#E3E9F3] dark:border-gray-700 rounded-xl p-4">
              <header className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-[13.5px] font-bold text-[#1E293B] dark:text-gray-100">{section.title}</h3>
                <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${issues.length
                  ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'}`}
                >
                  {issues.length ? `${issues.length} to check` : 'Complete'}
                </span>
              </header>
              <p className="text-[12px] text-[#94A3B8] mb-2 leading-relaxed">
                {issues.length ? issues[0].message : 'Essential fields completed'}
              </p>
              <button type="button" className="text-[12px] font-semibold text-blue-600 hover:text-blue-700" onClick={() => goTo(i)}>
                Review section →
              </button>
            </article>
          );
        })}
      </div>

      <section className="border-t border-[#E3E9F3] dark:border-gray-700 pt-5">
        <h3 className="text-[14px] font-bold text-[#1E293B] dark:text-gray-100 mb-3">Assessor recommendation</h3>
        <p className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-100">{answers.recommendation || 'Not recorded'}</p>
        {answers.recommendationReason && <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5 whitespace-pre-wrap">{answers.recommendationReason}</p>}
        {answers.recommendation === 'Suitable with conditions' && answers.recommendationConditions && (
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1.5"><strong>Conditions:</strong> {answers.recommendationConditions}</p>
        )}
        <label className="flex items-start gap-2.5 mt-4 text-[13px] text-[#475569] dark:text-gray-300 leading-relaxed">
          <input type="checkbox" className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
          <span>I have checked these answers and recorded my recommendation. I understand that the manager makes the final decision.</span>
        </label>
      </section>
    </>
  );

  const section = sections[step];
  const pct = overall.total ? Math.round((overall.done / overall.total) * 100) : 0;

  return (
    <Shell wide>
      <div className="space-y-5">
        {/* Who is asking, and what for — before anything is asked of them. */}
        <div className={cardCls}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center shrink-0">
              <ClipboardList className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-[19px] sm:text-[20px] font-bold text-[#1E293B] dark:text-gray-100">
                {setting?.type || 'Care assessment'} for {person.first_name} {person.surname}
              </h1>
              <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
                {organisation.name} has asked you to complete this assessment
                {request.care_requested ? ` for ${request.care_requested.toLowerCase()}` : ''}.
                Record your findings, then give your recommendation at the end.
              </p>
              {request.reason && (
                <p className="text-[13px] text-[#475569] dark:text-gray-300 mt-3 bg-[#F8FAFF] dark:bg-[#1f242b] rounded-xl p-3 leading-relaxed">
                  <strong className="text-[#1E293B] dark:text-gray-200">Reason for the enquiry:</strong> {request.reason}
                </p>
              )}
            </div>
          </div>

          {/* Progress across the whole assessment. */}
          <div className="mt-5 pt-4 border-t border-[#EDF1F7] dark:border-gray-700">
            <div className="flex items-center justify-between text-[12px] text-[#64748B] dark:text-gray-400 mb-1.5">
              <span>Essential fields</span>
              <strong className="text-[#1E293B] dark:text-gray-200">{pct}%</strong>
            </div>
            <div className="h-1.5 rounded-full bg-[#E7EDF7] dark:bg-gray-700 overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-[11.5px] text-[#94A3B8]">
                {savedAt ? `Draft saved ${new Date(savedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}` : 'Not saved yet'}
                {dirty ? ' · unsaved changes' : ''}
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                leftIcon={<Save className="w-3.5 h-3.5" />}
                onClick={() => send(false)}
                loading={saving}
                disabled={submitting}
              >
                Save draft
              </Button>
            </div>
          </div>
        </div>

        {/* Below the header: the section rail down the left on a desktop, the
            form beside it. On anything narrower the rail becomes the select
            above the form — an eleven-item list has no business eating the top
            half of a phone screen. */}
        <div className="lg:flex lg:items-start lg:gap-5">
          <aside className="hidden lg:block w-[256px] shrink-0 lg:sticky lg:top-6">
            <div className={`${cardCls} !p-3 max-h-[calc(100vh-3rem)] overflow-y-auto`}>
              <p className="px-2.5 pt-1 pb-2 text-[10px] font-bold tracking-[0.13em] text-[#94A3B8]">
                ASSESSMENT SECTIONS
              </p>
              <nav aria-label="Assessment sections" className="flex flex-col gap-0.5">
                {sections.map((sec, i) => {
                  const st = statsFor(i);
                  return (
                    <RailItem
                      key={sec.id}
                      n={i + 1}
                      title={sec.title}
                      subtitle={`${st.done} of ${st.total} essential fields`}
                      active={step === i}
                      complete={st.done === st.total && sectionIssues(i).length === 0}
                      onClick={() => goTo(i)}
                    />
                  );
                })}
                <RailItem
                  n={reviewStep + 1}
                  title="Review & return"
                  subtitle={allIssues.length
                    ? `${allIssues.length} field${allIssues.length === 1 ? '' : 's'} to fix`
                    : 'Ready to return'}
                  active={step === reviewStep}
                  complete={!allIssues.length}
                  onClick={() => goTo(reviewStep)}
                />
              </nav>
            </div>
          </aside>

          <div className="flex-1 min-w-0 space-y-5">
            <div className={`${cardCls} !p-3 lg:hidden`}>
              <label className="sr-only" htmlFor="stepSelect">Assessment section</label>
              <select
                id="stepSelect"
                className={inputCls}
                value={step}
                onChange={(e) => goTo(Number(e.target.value))}
              >
                {sections.map((sec, i) => {
                  const st = statsFor(i);
                  return <option key={sec.id} value={i}>{i + 1}. {sec.title} ({st.done}/{st.total})</option>;
                })}
                <option value={reviewStep}>{reviewStep + 1}. Review &amp; return</option>
              </select>
            </div>

            {/* The section itself. */}
            <div className={cardCls}>
              {step === reviewStep ? review() : (
                <>
                  <header className="mb-5">
                    <div className="text-[10px] font-bold tracking-wider text-blue-600 mb-2">
                      STEP {String(step + 1).padStart(2, '0')} OF {reviewStep + 1}
                    </div>
                    <h2 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">{section.title}</h2>
                    <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-1.5">{section.description}</p>
                  </header>

                  {section.groups.map((grp) => {
                    const fields = grp.fields.filter(visible);
                    if (!fields.length) return null;
                    return (
                      <section key={grp.title} className="mb-7 last:mb-0">
                        <h3 className="text-[14px] font-bold text-[#1E293B] dark:text-gray-100 mb-1">{grp.title}</h3>
                        {grp.note && (
                          <p className="text-[12.5px] text-[#6F85A6] dark:text-blue-300 bg-[#F5F8FF] dark:bg-blue-900/10 border border-[#E0E9FA] dark:border-blue-900/40 rounded-xl p-3 my-3 leading-relaxed">
                            {grp.note}
                          </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                          {fields.map(renderField)}
                        </div>
                      </section>
                    );
                  })}
                </>
              )}
            </div>

            {/* Navigation. On a phone this is Back and Next and nothing
                else: the assessor is one-handed on somebody's doorstep, and
                "Save draft" sits in the header beside the draft status rather
                than at the foot of a long section. */}
            <div className={`${cardCls} !p-3 sm:!p-5`}>
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  className="flex-1 sm:flex-none"
                  leftIcon={<ArrowLeft className="w-4 h-4" />}
                  onClick={() => goTo(step - 1)}
                  disabled={step === 0}
                >
                  Back
                </Button>
                <p className="hidden sm:block text-[12px] text-[#94A3B8] flex-1">
                  Fields marked * are needed before the assessment can be returned.
                </p>
                <Button
                  variant="secondary"
                  className="hidden sm:inline-flex"
                  onClick={() => send(false)}
                  loading={saving}
                  disabled={submitting}
                >
                  Save draft
                </Button>
                {step === reviewStep ? (
                  <Button variant="primary" className="flex-1 sm:flex-none" onClick={submit} loading={submitting} disabled={saving}>
                    Submit to manager
                  </Button>
                ) : (
                  <Button variant="primary" className="flex-1 sm:flex-none" rightIcon={<ArrowRight className="w-4 h-4" />} onClick={next}>
                    Next section
                  </Button>
                )}
              </div>
              <p className="sm:hidden text-[11.5px] text-[#94A3B8] mt-3 text-center leading-relaxed">
                Fields marked * are needed before the assessment can be returned.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default CareAssessmentForm;
